import {
    createTableColumn,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableColumnSizingOptions,
    TableRow,
    useTableColumnSizing_unstable,
    useTableFeatures,
} from '@fluentui/react-components';
import { CustomerMishandledBaggageBooking } from '../../../model/customerWithUpdates';
import { useTranslation } from 'react-i18next';
import { useGroupBookingTableStyles } from './BaggageInfoDialog.styles';
import { useMemo } from 'react';
import { TFunction } from 'i18next';
import { BaggageIcon } from '../../Icons/BaggageIcon';

const getGroupBookingTableConfig = (
    styles: Record<string, string>,
    t: TFunction<'translation', undefined>
) => {
    return [
        createTableColumn<CustomerMishandledBaggageBooking>({
            columnId: 'firstName,lastName',
            renderHeaderCell: () => <></>,
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        <span className={styles.customerDisplayName}>
                            <b>{`${item.customer.firstName} ${item.customer.lastName}`}</b>
                        </span>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerMishandledBaggageBooking>({
            columnId: 'seatNumber',
            renderHeaderCell: () => <></>,
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        <span className={styles.ceilContent}>
                            <span>
                                {t('TableHeader_Passenger_SeatNumber')}:
                            </span>
                            <b>{item.customer.seatNumber}</b>
                        </span>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerMishandledBaggageBooking>({
            columnId: 'cabin',
            renderHeaderCell: () => <></>,
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        <span className={styles.ceilContent}>
                            <span>{t('TableHeader_Passenger_Cabin')}:</span>
                            <b>{item.customer.cabin}</b>
                        </span>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerMishandledBaggageBooking>({
            columnId: 'frequentFlyerTier',
            renderHeaderCell: () => <></>,
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        <span className={styles.ceilContent}>
                            <span>
                                {t('TableHeader_Passenger_LoyaltyLevel')}:
                            </span>
                            <b>{item.customer.frequentFlyerTier}</b>
                        </span>
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerMishandledBaggageBooking>({
            columnId: 'status',
            renderHeaderCell: () => <></>,
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        <BaggageIcon
                            width={16}
                            height={16}
                            type={
                                item.bags.every(
                                    (b) =>
                                        b.notificationInfo.status === 'Notified'
                                )
                                    ? 'success'
                                    : 'danger'
                            }
                        />
                    </TableCellLayout>
                );
            },
        }),
    ];
};

const columnSizingOptions: TableColumnSizingOptions = {
    'firstName,lastName': {
        minWidth: 28,
    },
    seatNumber: {
        minWidth: 20,
    },
    cabin: {
        minWidth: 25,
    },
    frequentFlyerTier: {
        minWidth: 26,
    },
    status: {
        minWidth: 1,
    },
};

const GroupBookingTable = ({
    items,
    onSelect = () => {},
}: {
    items: CustomerMishandledBaggageBooking[];
    onSelect?: (item: CustomerMishandledBaggageBooking) => void;
}) => {
    const { t } = useTranslation();

    const styles = useGroupBookingTableStyles();

    const columns = useMemo(
        () => getGroupBookingTableConfig(styles, t),
        [styles, t]
    );

    const { getRows, columnSizing_unstable: columnSizing } = useTableFeatures(
        {
            getRowId: (row) => row.customer.id,
            columns,
            items,
        },
        [
            useTableColumnSizing_unstable({
                columnSizingOptions,
            }),
        ]
    );

    const rows = getRows((row) => {
        return {
            ...row,
            appearance: 'none' as const,
        };
    });

    return (
        <Table className={styles.root}>
            <TableBody>
                {rows &&
                    rows.map(({ item, appearance, rowId }) => (
                        <TableRow
                            className={styles.tableRow}
                            key={rowId}
                            appearance={appearance}
                            onClick={() => onSelect(item)}
                        >
                            {columns.map((column) => (
                                <TableCell
                                    key={column.columnId}
                                    tabIndex={0}
                                    role="gridcell"
                                    style={{
                                        ...columnSizing.getTableCellProps(
                                            column.columnId
                                        )?.style,
                                        userSelect: 'none',
                                    }}
                                >
                                    {column.renderCell(item)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
            </TableBody>
        </Table>
    );
};

export default GroupBookingTable;
